import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  listProducts,
  getProductBrands,
  toggleProductActive,
  toggleProductPeerVisibility,
  updateProductQty,
  type Product,
  type ProductPage,
  type ProductSearchField,
} from '@/api/products';
import { brand } from '@/theme/theme';
import { formatNearExpiry } from '@/utils/nearExpiry';
import { useAppSettings } from '@/hooks/useAppSettings';
import { PaginationFooter } from '@/components/PaginationFooter';

// ── types ─────────────────────────────────────────────────────────────────────

type StockFilter  = 'ALL' | 'IN_STOCK' | 'LOW_STOCK' | 'NO_STOCK';
type StatusFilter = 'ALL' | 'ACTIVE'   | 'INACTIVE';
type SortDir      = 'asc' | 'desc';

interface Filters {
  brands:       string[];
  stockFilter:  StockFilter;
  statusFilter: StatusFilter;
  schemeOnly:   boolean;
}

interface SortConfig { field: string; dir: SortDir }

const DEFAULT_FILTERS: Filters = {
  brands:       [],
  stockFilter:  'ALL',
  statusFilter: 'ALL',
  schemeOnly:   false,
};

// ── helpers ───────────────────────────────────────────────────────────────────

function rowBg(qty: number | null | undefined, low: number): string {
  const q = qty ?? 0;
  if (q === 0)    return '#FAC8C8';
  if (q <= low)   return '#FBCEB1';
  return '#E8FCE8';
}

function getDiscount(p: Product): string {
  if (p.discountPct != null && Number(p.discountPct) > 0)
    return `${Number(p.discountPct)}%`;
  return '—';
}

function getPack(p: Product): string {
  return p.packSize || p.uom || '—';
}

// ── ProductTooltip ─────────────────────────────────────────────────────────────

function ProductTooltip({ p }: { p: Product }) {
  return (
    <Box sx={{ p: 0.5, minWidth: 190 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.3 }}>{p.name}</Typography>
      {p.genericName && (
        <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.72)', mb: 0.2 }}>
          {p.genericName}
        </Typography>
      )}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', my: 0.5 }} />
      {([
        ['SKU',    p.sku],
        ['Brand',  p.brand  || '—'],
        ['Pack',   getPack(p)],
        ['MRP',    p.mrp    != null ? `₹${Number(p.mrp).toFixed(2)}`  : '—'],
        ['PTR',    p.ptr    != null ? `₹${Number(p.ptr).toFixed(2)}`  : '—'],
        ['PTS',    p.pts    != null ? `₹${Number(p.pts).toFixed(2)}`  : '—'],
        ['GST',    p.gstRate != null ? `${Number(p.gstRate).toFixed(0)}%` : '—'],
        ['HSN',    p.hsnCode || '—'],
        ['Scheme', p.scheme  || '—'],
        ['Stock',  p.availableQty != null ? String(p.availableQty) : '—'],
        ['Near Exp', formatNearExpiry(p.nearExpiry) || '—'],
      ] as [string, string][]).map(([lbl, val]) => (
        <Stack key={lbl} direction="row" spacing={1}>
          <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.58)', width: 54, flexShrink: 0 }}>
            {lbl}
          </Typography>
          <Typography sx={{ fontSize: '0.67rem' }}>{val}</Typography>
        </Stack>
      ))}
      {p.description && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', my: 0.5 }} />
          <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.68)' }}>
            {p.description}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ── FilterPanel ───────────────────────────────────────────────────────────────

function FilterPanel({
  filters, onChange, allBrands, lowThreshold,
}: {
  filters:      Filters;
  onChange:     (f: Filters) => void;
  allBrands:    string[];
  lowThreshold: number;
}) {
  const [brandSearch, setBrandSearch] = useState('');
  const [companyOpen, setCompanyOpen] = useState(true);

  const visibleBrands = allBrands.filter(b =>
    b.toLowerCase().includes(brandSearch.toLowerCase()),
  );

  const toggleBrand = (b: string) => {
    const next = filters.brands.includes(b)
      ? filters.brands.filter(x => x !== b)
      : [...filters.brands, b];
    onChange({ ...filters, brands: next });
  };

  const hasActive =
    filters.brands.length > 0 ||
    filters.stockFilter  !== 'ALL' ||
    filters.statusFilter !== 'ALL' ||
    filters.schemeOnly;

  // shared styles to keep things compact
  const SECTION_TITLE = { fontSize: '0.65rem', fontWeight: 700, color: brand.navy, textTransform: 'uppercase' as const, letterSpacing: '0.04em' };
  const ITEM_LABEL    = { fontSize: '0.64rem' };
  const CARD_SX       = { mb: 0.75, borderRadius: 1.5, overflow: 'hidden' } as const;
  const CARD_INNER    = { px: 1.2, py: 0.6 } as const;

  return (
    // Dropdown content rendered inside a Popover anchored to the Filters button
    <Box sx={{
      width: 240,
      maxHeight: 420,
      overflowY: 'auto',
      overflowX: 'hidden',
      p: 1.25,
      // thin custom scrollbar on the panel itself
      '&::-webkit-scrollbar':       { width: 3 },
      '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.18)', borderRadius: 4 },
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: brand.navy }}>Filters</Typography>
        {hasActive && (
          <Button size="small" sx={{ fontSize: '0.6rem', minHeight: 0, py: 0.15, px: 0.6, lineHeight: 1.4 }}
            onClick={() => onChange(DEFAULT_FILTERS)}>
            Clear all
          </Button>
        )}
      </Stack>

      {/* ── Company ─────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={CARD_SX}>
        <Stack
          direction="row" alignItems="center" justifyContent="space-between"
          onClick={() => setCompanyOpen(o => !o)}
          sx={{ px: 1.2, py: 0.6, cursor: 'pointer',
            bgcolor: companyOpen ? 'rgba(123,97,255,0.04)' : 'transparent' }}
        >
          <Typography sx={SECTION_TITLE}>Company</Typography>
          <Stack direction="row" alignItems="center" spacing={0.4}>
            {filters.brands.length > 0 && (
              <Chip label={filters.brands.length} size="small" color="primary"
                sx={{ height: 14, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.6 } }} />
            )}
            {companyOpen ? <ExpandLessIcon sx={{ fontSize: 13 }} /> : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
          </Stack>
        </Stack>

        <Collapse in={companyOpen}>
          {/* Compact search field */}
          <Box sx={{ px: 0.75, pt: 0.5, pb: 0.25 }}>
            <TextField
              placeholder="Search…"
              value={brandSearch}
              onChange={e => setBrandSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.3 }}>
                    <SearchIcon sx={{ fontSize: 11 }} />
                  </InputAdornment>
                ),
                endAdornment: brandSearch ? (
                  <InputAdornment position="end" sx={{ ml: 0 }}>
                    <IconButton size="small" onClick={() => setBrandSearch('')} sx={{ p: 0.2 }}>
                      <ClearIcon sx={{ fontSize: 10 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: {
                  height: 24,
                  fontSize: '0.62rem',
                  borderRadius: 1,
                  '& input': { py: 0, px: 0.4, fontSize: '0.62rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.18)' },
                },
              }}
              sx={{ width: '100%', '& .MuiInputBase-root': { pr: 0.4 } }}
            />
          </Box>

          {/* Clear selected */}
          {filters.brands.length > 0 && (
            <Box sx={{ px: 1.2, pb: 0.25 }}>
              <Button size="small" sx={{ fontSize: '0.58rem', minHeight: 0, py: 0, px: 0.4 }}
                onClick={() => onChange({ ...filters, brands: [] })}>
                Clear selection
              </Button>
            </Box>
          )}

          {/* Scrollable brand list — tall enough for ~15 items */}
          <Box sx={{
            height: 'calc(35vh - 60px)',
            minHeight: 160,
            maxHeight: 340,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 0.4,
            pb: 0.5,
            '&::-webkit-scrollbar':       { width: 3 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.18)', borderRadius: 4 },
          }}>
            {allBrands.length === 0
              ? <Typography sx={{ ...ITEM_LABEL, color: brand.inkSoft, px: 1, py: 0.5 }}>Loading…</Typography>
              : visibleBrands.length === 0
              ? <Typography sx={{ ...ITEM_LABEL, color: brand.inkSoft, px: 1, py: 0.5 }}>No match</Typography>
              : visibleBrands.map(b => (
                  <FormControlLabel key={b}
                    control={
                      <Checkbox checked={filters.brands.includes(b)} onChange={() => toggleBrand(b)}
                        size="small" sx={{ p: 0.3 }} />
                    }
                    label={
                      <Typography sx={{ ...ITEM_LABEL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b}
                      </Typography>
                    }
                    sx={{ display: 'flex', mx: 0, px: 0.4, py: 0.1, borderRadius: 0.75,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }, maxWidth: '100%' }}
                  />
                ))
            }
          </Box>
        </Collapse>
      </Card>

      {/* ── Stock ───────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={CARD_INNER}>
          <Typography sx={{ ...SECTION_TITLE, mb: 0.4 }}>Stock</Typography>
          <RadioGroup value={filters.stockFilter}
            onChange={e => onChange({ ...filters, stockFilter: e.target.value as StockFilter })}>
            {([
              ['ALL',       'All'],
              ['IN_STOCK',  'In Stock Only'],
              ['LOW_STOCK', `Low Stock (≤${lowThreshold})`],
              ['NO_STOCK',  'No Stock Only'],
            ] as [StockFilter, string][]).map(([val, label]) => (
              <FormControlLabel key={val} value={val}
                control={<Radio size="small" sx={{ p: 0.25 }} />}
                label={<Typography sx={ITEM_LABEL}>{label}</Typography>}
                sx={{ mx: 0, py: 0 }}
              />
            ))}
          </RadioGroup>
          <Typography sx={{ fontSize: '0.58rem', color: brand.inkSoft, mt: 0.3, lineHeight: 1.3 }}>
            Threshold: {lowThreshold} · set in <em>Profile → Settings</em>
          </Typography>
        </Box>
      </Card>

      {/* ── Schemes ─────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={CARD_INNER}>
          <Typography sx={{ ...SECTION_TITLE, mb: 0.4 }}>Schemes</Typography>
          <FormControlLabel
            control={
              <Checkbox checked={filters.schemeOnly}
                onChange={e => onChange({ ...filters, schemeOnly: e.target.checked })}
                size="small" sx={{ p: 0.25 }} />
            }
            label={<Typography sx={ITEM_LABEL}>Scheme Only</Typography>}
            sx={{ mx: 0, py: 0 }}
          />
        </Box>
      </Card>

      {/* ── Status ──────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ ...CARD_SX, mb: 0 }}>
        <Box sx={CARD_INNER}>
          <Typography sx={{ ...SECTION_TITLE, mb: 0.4 }}>Status</Typography>
          <RadioGroup value={filters.statusFilter}
            onChange={e => onChange({ ...filters, statusFilter: e.target.value as StatusFilter })}>
            {([
              ['ALL',      'All'],
              ['ACTIVE',   'Active Only'],
              ['INACTIVE', 'Inactive Only'],
            ] as [StatusFilter, string][]).map(([val, label]) => (
              <FormControlLabel key={val} value={val}
                control={<Radio size="small" sx={{ p: 0.25 }} />}
                label={<Typography sx={ITEM_LABEL}>{label}</Typography>}
                sx={{ mx: 0, py: 0 }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Card>
    </Box>
  );
}

// ── QtyCell ───────────────────────────────────────────────────────────────────

function QtyCell({ product, onSaved }: { product: Product; onSaved: (p: Product) => void }) {
  const [editing, setEditing] = useState(false);
  const [value,   setValue]   = useState(String(product.availableQty ?? 0));
  const [saving,  setSaving]  = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setValue(String(product.availableQty ?? 0)), [product.availableQty]);

  const save = useCallback(async () => {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) { setValue(String(product.availableQty ?? 0)); setEditing(false); return; }
    if (qty === (product.availableQty ?? 0)) { setEditing(false); return; }
    setSaving(true);
    try {
      onSaved(await updateProductQty(product.id, qty));
    } catch {
      setValue(String(product.availableQty ?? 0));
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }, [value, product, onSaved]);

  if (editing) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <input
          ref={inputRef} type="number" min={0} value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter') inputRef.current?.blur();
            if (e.key === 'Escape') { setValue(String(product.availableQty ?? 0)); setEditing(false); }
          }}
          autoFocus
          style={{ width: 64, fontSize: '0.75rem', padding: '2px 4px',
            border: `1.5px solid ${brand.violet}`, borderRadius: 4, outline: 'none', textAlign: 'right' }}
        />
        {saving && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
      </Box>
    );
  }

  return (
    <Box component="span" onClick={() => setEditing(true)}
      sx={{ cursor: 'text', px: 0.5, borderRadius: 0.5,
        '&:hover': { bgcolor: 'rgba(123,97,255,0.1)', outline: `1px dashed ${brand.violet}` } }}>
      {product.availableQty ?? 0}
    </Box>
  );
}

// ── StatusChip ────────────────────────────────────────────────────────────────

function StatusChip({ product, onToggled }: { product: Product; onToggled: (p: Product) => void }) {
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [errMsg,      setErrMsg]      = useState<string | null>(null);

  // Clear optimistic once parent delivers the real value
  useEffect(() => { setOptimistic(null); }, [product.isActive]);

  const isActive = optimistic !== null ? optimistic : product.isActive !== false;

  const doToggle = async (currentlyActive: boolean) => {
    setOptimistic(!currentlyActive);        // instant visual feedback
    setLoading(true); setErrMsg(null);
    try {
      onToggled(await toggleProductActive(product.id));
    } catch {
      setOptimistic(currentlyActive);       // revert on failure
      setErrMsg('Update failed. Try again.');
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Tooltip title={errMsg || ''} open={!!errMsg} placement="top" arrow>
        <Chip
          size="small"
          label={loading ? '…' : isActive ? 'Active' : 'Inactive'}
          color={isActive ? 'success' : 'default'}
          variant={isActive ? 'filled' : 'outlined'}
          onClick={() => isActive ? setConfirmOpen(true) : doToggle(false)}
          sx={{
            cursor: 'pointer',
            fontSize: '0.68rem',
            height: 20,
            ...(!isActive && {
              color: '#b91c1c',
              borderColor: '#b91c1c',
              bgcolor: 'rgba(185,28,28,0.07)',
            }),
          }}
        />
      </Tooltip>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Deactivate Product?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{product.name}</strong> will be hidden from the ordering system.
            You can reactivate it anytime by clicking Inactive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
          <Button size="small" color="error" variant="contained"
            onClick={() => doToggle(true)} disabled={loading}>
            {loading ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── column definitions ────────────────────────────────────────────────────────

const COLS = [
  { id: 'sno',     label: '#',            sortField: undefined,      w: 40,     align: 'center' },
  { id: 'sku',     label: 'SKU',          sortField: 'sku',          w: 80,     align: 'left'   },
  { id: 'name',    label: 'Product Name', sortField: 'name',         w: 'auto', align: 'left'   },
  { id: 'brand',   label: 'Brand',        sortField: 'brand',        w: 90,     align: 'left'   },
  { id: 'pack',    label: 'Pack',         sortField: undefined,      w: 70,     align: 'left'   },
  { id: 'ne',      label: 'Near Exp',     sortField: undefined,      w: 88,     align: 'left'   },
  { id: 'mrp',     label: 'MRP',          sortField: 'mrp',          w: 78,     align: 'right'  },
  { id: 'ptr',     label: 'PTR',          sortField: 'ptr',          w: 78,     align: 'right'  },
  { id: 'pts',     label: 'PTS',          sortField: 'pts',          w: 78,     align: 'right'  },
  { id: 'scheme',  label: 'Scheme',       sortField: undefined,      w: 64,     align: 'left'   },
  { id: 'disc',    label: 'Disc%',        sortField: undefined,      w: 52,     align: 'right'  },
  { id: 'qty',     label: 'Qty',          sortField: 'availableQty', w: 60,     align: 'right'  },
  { id: 'status',  label: 'Status',       sortField: undefined,      w: 68,     align: 'left'   },
  { id: 'actions', label: 'Actions',      sortField: undefined,      w: 88,     align: 'center' },
] as const;

// ── ProductsPage ──────────────────────────────────────────────────────────────

interface LocState { created?: boolean; updated?: boolean; stockAdded?: boolean }

const CELL: object = { fontSize: '0.75rem', py: '2px', px: '8px', lineHeight: 1.3 };
const HEAD: object = { ...CELL, fontWeight: 700, bgcolor: 'rgba(11,21,48,0.03)', whiteSpace: 'nowrap' };

export function ProductsPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const locState  = location.state as LocState | undefined;

  // ── search — explicit icon/Enter trigger, never live-as-you-type ──────
  const [searchInput, setSearchInput] = useState('');
  const [searchField, setSearchField] = useState<ProductSearchField>('name');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<ProductSearchField>('name');

  // ── filter / sort / pagination state — all sent to the backend ────────
  const { settings } = useAppSettings();
  const lowThreshold = settings.lowStockThreshold;

  const [filters,    setFilters]  = useState<Filters>(DEFAULT_FILTERS);
  const [sort,       setSort]     = useState<SortConfig>({ field: 'name', dir: 'asc' });
  const [page,       setPage]     = useState(0);
  const [size,       setSize]     = useState(50);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // ── data — a single backend-filtered, backend-sorted, backend-paginated page ──
  const [data,    setData]    = useState<ProductPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await listProducts(page, size, {
        search: appliedSearch || undefined,
        searchField: appliedSearch ? appliedSearchField : undefined,
        brands: filters.brands.length > 0 ? filters.brands : undefined,
        schemeOnly: filters.schemeOnly || undefined,
        stockFilter: filters.stockFilter !== 'ALL' ? filters.stockFilter : undefined,
        lowStockThreshold: filters.stockFilter === 'LOW_STOCK' ? lowThreshold : undefined,
        statusFilter: filters.statusFilter !== 'ALL' ? filters.statusFilter : undefined,
        sortBy: sort.field,
        sortDir: sort.dir,
      });
      setData(result);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Could not load products.');
    } finally {
      setLoading(false);
    }
  }, [page, size, appliedSearch, appliedSearchField, filters, sort, lowThreshold]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
    setAppliedSearchField(searchField);
    setPage(0);
  };

  // ── toasts ────────────────────────────────────────────────────────────
  const [createdToast, setCreatedToast] = useState(false);
  const [updatedToast, setUpdatedToast] = useState(false);
  const [stockToast,   setStockToast]   = useState(false);

  useEffect(() => {
    if (locState?.created)    { setCreatedToast(true); navigate(location.pathname, { replace: true, state: {} }); }
    else if (locState?.updated)    { setUpdatedToast(true); navigate(location.pathname, { replace: true, state: {} }); }
    else if (locState?.stockAdded) { setStockToast(true);   navigate(location.pathname, { replace: true, state: {} }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── brands for the filter panel — fetched once, independent of the current page ──
  const [allBrands, setAllBrands] = useState<string[]>([]);
  useEffect(() => { getProductBrands().then(setAllBrands).catch(() => {}); }, []);

  // Reset page when filter/sort changes (search resets page itself, on trigger)
  useEffect(() => { setPage(0); }, [filters, sort]);

  const pageRows = data?.content ?? [];

  // ── product update callback ───────────────────────────────────────────
  const handleUpdated = useCallback((updated: Product) => {
    setData(prev => prev ? { ...prev, content: prev.content.map(p => p.id === updated.id ? updated : p) } : prev);
  }, []);

  const handleSort = (field: string) =>
    setSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' },
    );

  const handleBlockToggle = useCallback(async (product: Product) => {
    try {
      handleUpdated(await toggleProductPeerVisibility(product.id));
    } catch {
      // ignore — user will see no change
    }
  }, [handleUpdated]);

  const activeFilterCount =
    (filters.brands.length > 0 ? 1 : 0) +
    (filters.stockFilter  !== 'ALL' ? 1 : 0) +
    (filters.statusFilter !== 'ALL' ? 1 : 0) +
    (filters.schemeOnly ? 1 : 0);

  // ── render ────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2} sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h2" sx={{ color: brand.navy, mb: 0.3 }}>Products</Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft }}>
            {loading ? 'Loading…' : `${data?.totalElements ?? 0} products`}
          </Typography>
        </Box>

        {/* Search — centred in the header bar between title and action buttons */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={searchField}
            onChange={e => setSearchField(e.target.value as ProductSearchField)}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="code">Code</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
          </Select>
          <TextField
            size="small" placeholder={`Search by ${searchField}…`}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput && (
                    <IconButton size="small" onClick={() => setSearchInput('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={handleSearch} aria-label="search">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <IconButton onClick={load} aria-label="refresh" size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button
            variant={filterAnchorEl ? 'contained' : 'outlined'}
            startIcon={<FilterIcon />}
            size="small"
            onClick={e => setFilterAnchorEl(a => (a ? null : e.currentTarget))}
            sx={{ fontSize: '0.75rem' }}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
          <Button
            variant="contained" startIcon={<AddIcon />} size="small"
            onClick={() => navigate('/products/new')}
            sx={{ fontSize: '0.75rem' }}
          >
            New Product
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Filter dropdown — anchored to the Filters button, not a persistent sidebar */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <FilterPanel
          filters={filters} onChange={setFilters}
          allBrands={allBrands}
          lowThreshold={lowThreshold}
        />
      </Popover>

      <Box>
        {/* Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : pageRows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: brand.inkSoft, mb: 2 }}>
                  {activeFilterCount > 0 || appliedSearch
                    ? 'No products match the filters.'
                    : 'No products yet.'}
                </Typography>
                {!activeFilterCount && !appliedSearch && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/products/new')}>
                    Add your first product
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }}>
                    <TableHead>
                      <TableRow>
                        {COLS.map(col => (
                          <TableCell key={col.id}
                            sx={{ ...HEAD, width: col.w, textAlign: col.align }}>
                            {col.sortField ? (
                              <TableSortLabel
                                active={sort.field === col.sortField}
                                direction={sort.field === col.sortField ? sort.dir : 'asc'}
                                onClick={() => handleSort(col.sortField!)}
                                sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                              >
                                {col.label}
                              </TableSortLabel>
                            ) : col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((p, idx) => (
                        <Tooltip key={p.id} title={<ProductTooltip p={p} />}
                          placement="right" arrow enterDelay={600}>
                          <TableRow hover
                            sx={{ bgcolor: rowBg(p.availableQty, lowThreshold),
                              '&:hover': { filter: 'brightness(0.97)' } }}>

                            {/* # */}
                            <TableCell sx={{ ...CELL, textAlign: 'center', color: '#999', fontSize: '0.7rem' }}>
                              {page * size + idx + 1}
                            </TableCell>

                            {/* SKU */}
                            <TableCell sx={{ ...CELL, fontFamily: 'monospace', fontSize: '0.7rem',
                              color: brand.violet, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.sku}
                            </TableCell>

                            {/* Product Name */}
                            <TableCell sx={{ ...CELL, whiteSpace: 'normal', minWidth: 140 }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2 }}>
                                {p.name}
                              </Typography>
                              {p.genericName && (
                                <Typography sx={{ fontSize: '0.65rem', color: brand.inkSoft, lineHeight: 1.2 }}>
                                  {p.genericName}
                                </Typography>
                              )}
                            </TableCell>

                            {/* Brand */}
                            <TableCell sx={{ ...CELL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.brand || '—'}
                            </TableCell>

                            {/* Pack */}
                            <TableCell sx={{ ...CELL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getPack(p)}
                            </TableCell>

                            {/* Near Expiry */}
                            <TableCell sx={{ ...CELL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatNearExpiry(p.nearExpiry) || '—'}
                            </TableCell>

                            {/* MRP */}
                            <TableCell sx={{ ...CELL, textAlign: 'right', fontWeight: 600 }}>
                              {p.mrp != null ? `₹${Number(p.mrp).toFixed(0)}` : '—'}
                            </TableCell>

                            {/* PTR */}
                            <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                              {p.ptr != null ? `₹${Number(p.ptr).toFixed(0)}` : '—'}
                            </TableCell>

                            {/* PTS */}
                            <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                              {p.pts != null ? `₹${Number(p.pts).toFixed(0)}` : '—'}
                            </TableCell>

                            {/* Scheme */}
                            <TableCell sx={CELL}>
                              {p.scheme
                                ? <Chip size="small" label={p.scheme} color="primary" variant="outlined"
                                    sx={{ fontSize: '0.8rem', height: 22, fontFamily: 'monospace', fontWeight: 500, '& .MuiChip-label': { fontWeight: 500 } }} />
                                : <Typography component="span" sx={{ fontSize: '0.75rem', color: brand.inkSoft }}>—</Typography>
                              }
                            </TableCell>

                            {/* Disc% */}
                            <TableCell sx={{ ...CELL, textAlign: 'right', color: '#16a34a', fontWeight: 500 }}>
                              {getDiscount(p)}
                            </TableCell>

                            {/* Qty */}
                            <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                              <QtyCell product={p} onSaved={handleUpdated} />
                            </TableCell>

                            {/* Status */}
                            <TableCell sx={CELL}>
                              <StatusChip product={p} onToggled={handleUpdated} />
                            </TableCell>

                            {/* Actions */}
                            <TableCell sx={{ ...CELL, textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <Tooltip title="Edit product">
                                <IconButton
                                  size="small"
                                  onClick={e => { e.stopPropagation(); navigate(`/products/${p.id}/edit`); }}
                                  sx={{ p: 0.4, color: brand.violet }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={p.isBlockedFromPeers ? 'Unblock from peers' : 'Block from peers'}>
                                <IconButton
                                  size="small"
                                  onClick={e => { e.stopPropagation(); handleBlockToggle(p); }}
                                  sx={{ p: 0.4, color: p.isBlockedFromPeers ? '#b91c1c' : 'text.disabled' }}
                                >
                                  <BlockIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </Tooltip>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <PaginationFooter
                  page={page}
                  totalPages={data?.totalPages ?? 1}
                  total={data?.totalElements ?? 0}
                  rowsPerPage={size}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  onPageChange={setPage}
                  onRowsPerPageChange={setSize}
                  label="products"
                />
              </>
            )}
          </Card>

          {/* Legend */}
          <Stack direction="row" spacing={2} sx={{ mt: 1, px: 1 }}>
            {[
              { bg: '#fff1f2', border: '1px solid #fecdd3', label: 'No stock' },
              { bg: '#fff7ed', border: '1px solid #fed7aa', label: `Low stock (1–${lowThreshold})` },
              { bg: '#f0fdf4', border: '1px solid #bbf7d0', label: 'In stock' },
            ].map(({ bg, label, border }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: bg,
                  border, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.65rem', color: brand.inkSoft }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Toasts */}
      {([
        { open: createdToast, set: setCreatedToast, msg: 'Product created successfully.' },
        { open: updatedToast, set: setUpdatedToast, msg: 'Product updated successfully.' },
        { open: stockToast,   set: setStockToast,   msg: 'Stock added successfully.'    },
      ] as const).map(({ open, set, msg }) => (
        <Snackbar key={msg} open={open} autoHideDuration={3500} onClose={() => set(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity="success" onClose={() => set(false)}>{msg}</Alert>
        </Snackbar>
      ))}
    </Box>
  );
}
